import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import { displayPrice } from 'helpers'
import Fonts from 'components/base-ui/Fonts'
import Label from './Label'
import media from 'styles/media'
import { useTranslation } from 'react-i18next'
import { getResizableURL } from 'helpers/image'

interface ModifierCartItem {
  name: string
  priceSatangs: number
  quantity: number
  multipleQuantity: boolean
}

export interface StatusCartItem {
  label: string
  backgroundColor: string
}

export interface ProductCartItem {
  images: {
    resizableURL: string
  }[]
  name: string
  modifiers: ModifierCartItem[]
  totalPriceSatangs: number
  quantity: number
  status?: StatusCartItem
  excludedFromPromotion: boolean
  buffetPackageName?: string
  customNotes: string
}
interface Props {
  product: ProductCartItem
}

const { screenXsMax } = media

const CartItem: FC<Props> = ({ product }) => {
  const { t } = useTranslation()
  const modifiers = useMemo(() => {
    return product.modifiers
      .map(({ name, quantity, multipleQuantity }) => {
        return multipleQuantity ? `${name} x${quantity}` : name
      })
      .join(', ')
  }, [product.modifiers])

  const productName = `x${product.quantity} ${product.name}`
  const totalPrice = displayPrice(product.totalPriceSatangs / 100)
  const hasModifier = modifiers.length > 0
  const imageURL1x = getResizableURL(product.images, { width: '250', height: '250', formatType: '_fmt-jpg' })
  const imageURL2x = getResizableURL(product.images, { width: '330', height: '330', formatType: '_fmt-jpg' })
  const imageURL3x = getResizableURL(product.images, { width: '375', height: '375', formatType: '_fmt-jpg' })

  return (
    <Wrapper>
      <ProductThumbnailWrapper>
        <ProductThumbnail image1x={imageURL1x} image2x={imageURL2x} image3x={imageURL3x} />
      </ProductThumbnailWrapper>
      <ProductDetails>
        <ProductName title={productName}>{productName}</ProductName>
        {hasModifier && <Modifiers>{modifiers}</Modifiers>}
        {product.customNotes && <CustomNotes>{`${t('remark')} : ${product.customNotes}`}</CustomNotes>}
        {product.excludedFromPromotion && (
          <LabelExcludedPromotion>{t('productExcludedPromotionShort')}</LabelExcludedPromotion>
        )}

        <MainDetails hasModifier={hasModifier}>
          {product.buffetPackageName ? (
            <PackageName>{product.buffetPackageName}</PackageName>
          ) : (
            <Price hasModifier={hasModifier}>฿{totalPrice}</Price>
          )}

          {product.status && (
            <StatusWrapper hasModifier={hasModifier}>
              <StatusLabel label={product.status.label} backgroundColor={product.status.backgroundColor} />
            </StatusWrapper>
          )}
        </MainDetails>
      </ProductDetails>
    </Wrapper>
  )
}

export default CartItem

const Wrapper = styled.div`
  display: flex;
  align-items: flex-start;
  column-gap: ${({ theme }) => theme.spacing.XL};

  @media (${screenXsMax}) {
    column-gap: ${({ theme }) => theme.spacing.XS};
    animation: fadeIn 1s;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      visibility: hidden;
    }

    to {
      opacity: 1;
      visibility: visible;
    }
  }
`

const ProductName = styled(Fonts.BaseTitleBold)`
  @media (${screenXsMax}) {
    font-size: ${({ theme }) => theme.fontSize.md};
  }
`

const Price = styled(Fonts.BaseTitleBold)<{ hasModifier: boolean }>`
  align-self: ${({ hasModifier }) => (hasModifier ? 'center' : 'flex-start')};

  @media (${screenXsMax}) {
    font-size: ${({ theme }) => theme.fontSize.md};
  }
`

const PackageName = styled(Fonts.SmallTitleRegular)`
  font-size: 11px;
  padding-top: 5px;
  font-style: italic;
  align-self: flex-start;
  color: ${({ theme }) => theme.colors.darkGrey};
`

const ProductThumbnailWrapper = styled.div``
const ProductThumbnail = styled.div<{ image1x: string; image2x: string; image3x: string }>`
  width: 90px;
  height: 90px;
  object-fit: contain;
  border-radius: ${({ theme }) => theme.radius.md};
  background-size: contain;

  @media (${screenXsMax}) {
    width: 65px;
    height: 65px;
  }
  background-image: -webkit-image-set(
    url(${({ image1x }) => `'${image1x}'`}) 1x,
    url(${({ image2x }) => `'${image2x}'`}) 2x,
    url(${({ image3x }) => `'${image3x}'`}) 3x
  );
  background-image: image-set(
    url(${({ image1x }) => `'${image1x}'`}) 1x,
    url(${({ image2x }) => `'${image2x}'`}) 2x,
    url(${({ image3x }) => `'${image3x}'`}) 3x
  );
`
const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.spacing['4XS']};
  justify-content: space-between;
  width: 100%;
  height: 100%;
`

const MainDetails = styled.div<{ hasModifier: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: ${({ hasModifier }) => (hasModifier ? 'inherit' : 1)};
`

const Modifiers = styled(Fonts.SmallTitleRegular)`
  width: 75%;
  color: ${({ theme }) => theme.colors.base};
  width: calc(100vw - (90px + 18px + 18px + 16px));

  @media (${screenXsMax}) {
    font-size: ${({ theme }) => theme.fontSize.md};
    width: calc(100vw - (65px + 18px + 18px + 16px));
  }
`

const CustomNotes = styled(Modifiers)`
  overflow: hidden;
  display: -webkit-box;
  line-clamp: 2;
  box-orient: vertical;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

const StatusLabel = styled(Label)`
  margin-top: 5px;
`

const StatusWrapper = styled.div<{ hasModifier: boolean }>`
  flex-shrink: 0;
  align-self: ${({ hasModifier }) => (hasModifier ? 'center' : 'flex-end')};
`

const LabelExcludedPromotion = styled(Fonts.SmallTitleMedium)`
  margin-top: ${({ theme }) => theme.spacing['4XS']};
  color: ${({ theme }) => theme.colors.infoDark};
`
